import { LoadingState } from "@/ds/custom/LoadingState";
import { DataPoint, DataPointGroup } from "@/ds/custom/MediaObject";
import { MoneyDisplay } from "@/ds/custom/MoneyDisplay";
import { Alert, AlertDescription, AlertTitle } from "@/ds/shadcn/components/ui/alert";
import { TableCell } from "@/ds/shadcn/components/ui/table";
import { ListItem } from "@/features/common/components/dataview/ListItem";
import { DataView, GalleryCard } from "@/features/common/components/dataview/DataView";
import { ChartHistoryDurationSelector } from "@/features/common/components/ChartHistoryDurationSelector";
import { StandardLineChart } from "@/features/common/components/StandardLineChart";
import { useEvItemsBreakdown, type EvItemBreakdownResponseModelCategory } from "@/features/ev/api/useEvItemBreakdown";
import { useEvHistoricalPositions } from "@/features/ev/api/useEvItemHistoricalPositions";
import { EvItemBreakdownTitleDisplay } from "@/features/ev/components/EvItemBreakdownTitleDisplay";
import type { EvProductType } from "@/features/ev/EvProductType";
import type { EvSetType } from "@/features/ev/EvSetType";
import { getEvHistoricalPricingChartConfig } from "@/features/ev/getEvHistoricalPricingChartConfig";
import { getEvHistoricalPricingChartData } from "@/features/ev/getEvHistoricalPricingChartData";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { useUserPreferencesState } from "@/lib/state/UserPreferencesState";
import { TriangleAlertIcon } from "lucide-react";
import React, { useCallback, useMemo, type FC } from "react";
import { Checkbox } from "@/ds/shadcn/components/ui/checkbox";
import type { ChartHistoryDuration } from "@/lib/domain/ChartHistoryDuration";

export interface EvItemBreakdownProps {
	evSetType: EvSetType;
	evProductType: EvProductType;
	onSelectCategory: (category: EvItemBreakdownResponseModelCategory) => void;
}

export const EvItemBreakdown = (props: EvItemBreakdownProps) => {
	const {
		evSetType,
		evProductType,
		onSelectCategory
	} = props;

	const { state, dispatchSetEvExcludedCategories } = useUserPreferencesState();
	const evItemsBreakdown = useEvItemsBreakdown(evSetType, evProductType);
	const evItemHisoricalPositions = useEvHistoricalPositions(evSetType, evProductType, getEvGraphAdjustedDays(state.chartHistoryDuration));
	const chartData = useMemo(() => getEvHistoricalPricingChartData(evItemHisoricalPositions.data || []), [evItemHisoricalPositions.data]);
	const chartConfig = useMemo(() => getEvHistoricalPricingChartConfig(), []);

	const currentEstimatedValue = useMemo(() => {
		return evItemsBreakdown.data && {
			value: (evItemsBreakdown.data.categories ?? []).reduce(
				(total, category) =>
					state.evExcludedCategories[evSetType]?.includes(category.category)
						? total
						: total + category.contributedValue.value,
				0
			),
			currency: evItemsBreakdown.data.currentEstimatedValue.currency
		};
	}, [evItemsBreakdown.data, state.evExcludedCategories, evSetType]);

	const additionalProps = useMemo(() => ({
		evExcludedCategories: state.evExcludedCategories,
		evSetType,
		onSelectCategory,
		dispatchSetEvExcludedCategories
	}), [state.evExcludedCategories, evSetType, onSelectCategory, dispatchSetEvExcludedCategories]);

	if (evItemsBreakdown.isError) {
		return (
			<div className="p-3">
				<ErrorDisplay error={evItemsBreakdown.error} />
			</div>
		);
	}

	if (!evItemsBreakdown.isSuccess || !currentEstimatedValue) {
		return <LoadingState />;
	}

	return (
		<>
			{
				evItemsBreakdown.data.warning &&
				<div className="p-3 border-b">
					<Alert variant="warning">
						<TriangleAlertIcon />
						<AlertTitle>Warning</AlertTitle>
						<AlertDescription>{evItemsBreakdown.data.warning}</AlertDescription>
					</Alert>
				</div>
			}

			<EvItemBreakdownTitleDisplay
				imageUrl={evItemsBreakdown.data.imageUrl}
				productName={evItemsBreakdown.data.productName}
				fcId={evItemsBreakdown.data.fcId}
				currentEstimatedValue={currentEstimatedValue} />

			<ChartHistoryDurationSelector />

			<StandardLineChart data={chartData} config={chartConfig} xAxisDataKey="date" />

			<DataView
				onlyUseGalleryForMobile
				items={evItemsBreakdown.data.categories}
				getKey={x => x.categoryName}
				listViewHeaderLabels={["Category", "Average Market Price", "Pull Rate", "Contributed Value", ""]}
				listViewHeaderClassNames={["min-w-0", "w-40 @md:w-52 @lg:w-64", "w-20 @md:w-31 @lg:w-44", "w-40 @md:w-52 @lg:w-64", "w-10"]}
				listComponent={EvItemBreakdownListView}
				galleryComponent={EvItemBreakdownGalleryView}
				additionalProps={additionalProps} />
		</>
	);
};

interface EvItemBreakdownViewProps extends EvItemBreakdownResponseModelCategory {
	evExcludedCategories: Partial<Record<EvSetType, string[]>>;
	evSetType: EvSetType;
	onSelectCategory: (category: EvItemBreakdownResponseModelCategory) => void;
	dispatchSetEvExcludedCategories: (excludedCategories: Partial<Record<EvSetType, string[]>>) => void;
}

const EvItemBreakdownListView: FC<EvItemBreakdownViewProps> = props => {
	const {
		evExcludedCategories,
		category,
		categoryName,
		averageMarketPrice,
		multiplier,
		contributedValue,
		evSetType,
		onSelectCategory,
		dispatchSetEvExcludedCategories
	} = props;

	const onClick = useCallback(() => onSelectCategory(props), [onSelectCategory, props]);

	return (
		<ListItem onClick={onClick}>
			<TableCell className="truncate">
				{categoryName}
			</TableCell>
			<TableCell>
				<MoneyDisplay money={averageMarketPrice} mode="Standard" />
			</TableCell>
			<TableCell>
				{multiplier}
			</TableCell>
			<TableCell>
				<MoneyDisplay money={contributedValue} mode="Standard" />
			</TableCell>
			<TableCell>
				<EvItemBreakdownCheckbox
					category={category}
					evSetType={evSetType}
					evExcludedCategories={evExcludedCategories}
					dispatchSetEvExcludedCategories={dispatchSetEvExcludedCategories} />
			</TableCell>
		</ListItem>
	);
};

const EvItemBreakdownGalleryView: FC<EvItemBreakdownViewProps> = props => {
	const {
		evExcludedCategories,
		category,
		categoryName,
		averageMarketPrice,
		multiplier,
		contributedValue,
		evSetType,
		onSelectCategory,
		dispatchSetEvExcludedCategories
	} = props;

	const onClick = useCallback(() => onSelectCategory(props), [onSelectCategory, props]);

	return (
		<GalleryCard onClick={onClick}>
			<div className="flex justify-between items-center p-2">
				<span className="type-item">{categoryName}</span>

				<EvItemBreakdownCheckbox
					category={category}
					evSetType={evSetType}
					evExcludedCategories={evExcludedCategories}
					dispatchSetEvExcludedCategories={dispatchSetEvExcludedCategories} />
			</div>

			<DataPointGroup>
				<DataPoint label="Average Market Price">
					<MoneyDisplay money={averageMarketPrice} mode="Standard" />
				</DataPoint>
				<DataPoint label="Pull Rate">
					{multiplier}
				</DataPoint>
				<DataPoint label="Contributed Value">
					<MoneyDisplay money={contributedValue} mode="Standard" />
				</DataPoint>
			</DataPointGroup>
		</GalleryCard>
	);
};

interface EvItemBreakdownCheckboxProps {
	category: string;
	evSetType: EvSetType;
	evExcludedCategories: Partial<Record<EvSetType, string[]>>;
	dispatchSetEvExcludedCategories: (excludedCategories: Partial<Record<EvSetType, string[]>>) => void;
}

const EvItemBreakdownCheckbox = (props: EvItemBreakdownCheckboxProps): React.JSX.Element => {
	const {
		category,
		evExcludedCategories,
		evSetType,
		dispatchSetEvExcludedCategories
	} = props;

	const excludedCategories = evExcludedCategories[evSetType] ?? [];
	const isChecked = !excludedCategories.includes(category);

	const onCheckedChange = useCallback((value: boolean) => {
		const newExclusions = (
			value
				? excludedCategories.filter(x => x !== category)
				: [...excludedCategories ?? [], category]
		);

		dispatchSetEvExcludedCategories({
			...evExcludedCategories,
			[evSetType]: newExclusions
		});
	}, [category, evExcludedCategories, evSetType, dispatchSetEvExcludedCategories]);

	return (
		<Checkbox
			className="text-primary align-middle"
			checked={isChecked}
			onClick={e => e.stopPropagation()}
			onCheckedChange={onCheckedChange} />
	);
};

const getEvGraphAdjustedDays = (userPreferencesChartHistoryDuration: ChartHistoryDuration) => {
	const days = Number(userPreferencesChartHistoryDuration);

	if (days <= 92) {
		return days + 1;
	} else if (days <= 365) {
		return days + 8;
	} else {
		return days + 32;
	}
};
